/**
 * Date formatting helpers (BUILD_SPEC.md §3.2 — date-fns + Asia/Amman).
 *
 * All API responses serialize timestamps as ISO-8601 UTC; the client
 * must format them in Asia/Amman to match what the office sees on the
 * municipality portal and on the wall clock. We funnel every display
 * date through these helpers so the timezone choice lives in one
 * place and a future move (say, to Asia/Riyadh) is a one-line edit.
 */

import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";

const TIMEZONE = "Asia/Amman";

/**
 * Returns minutes elapsed since the given timestamp. The TransactionCard
 * "recently changed" highlight (§10.3) uses this — anything < 5 minutes
 * gets the pulsing-border treatment.
 */
export function minutesSince(date: Date | string | null | undefined): number {
  if (!date) return Number.POSITIVE_INFINITY;
  const d = typeof date === "string" ? new Date(date) : date;
  return (Date.now() - d.getTime()) / 60_000;
}

/** "DD/MM/YYYY" in Asia/Amman — the format the office is used to. */
export function formatActionDate(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return format(toZonedTime(d, TIMEZONE), "dd/MM/yyyy");
}

/** "HH:mm" in Asia/Amman — used alongside the date for fresh entries. */
export function formatActionTime(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return format(toZonedTime(d, TIMEZONE), "HH:mm");
}

/**
 * Localized "X minutes ago" / "just now" / etc. The dashboard uses
 * this for the card footer (§10.3 footer: `آخر تحديث: <relative>`).
 *
 * Pure function over `minutesSince` so the result is locale-aware via
 * the i18n strings the caller passes in. We do NOT use date-fns's
 * `formatDistance` because it doesn't ship Arabic out of the box and
 * three-bucket precision is plenty for a TV display.
 */
export function relativeBucket(minutes: number): "now" | "minutes" | "hours" | "days" {
  if (minutes < 1) return "now";
  if (minutes < 60) return "minutes";
  if (minutes < 60 * 24) return "hours";
  return "days";
}
